import React from 'react';
import { css } from '@emotion/react';
import { Grid, TextField, IconButton, InputAdornment } from '@material-ui/core';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import DeleteForeverOutlined from '@material-ui/icons/DeleteForeverOutlined';
import { PrimaryButton } from '@/styles/blocks';
import { Body1 } from './blocks';
import { generateFuelBuckets } from './fuelUtils';

import FuelModal from './FuelModal';
import QboTagField from './QboTagField';

const dollarFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

// Generic fuel-price-indexed bucket editor. Renders a table of `min -> max` fuel-price
// bands, each mapping to a rate. Shared by the Modifier (%), Per Item ($/item) and Per
// Mile ($/mile) fuel-surcharge modes, which differ only in:
//   - `type`: the FUEL_PRICE_* rate key (rates[type], and passed to every callback)
//   - `isPercentage`: true stores the rate as a fraction (shown ×100, written ÷100, `%`
//     adornment); false stores whole dollars (`$` adornment)
//   - `fuelModalMode`: forwarded to <FuelModal mode> for the "Add Calculated FSC" helper
const FuelBucketEditor = ({ type, isPercentage = false, fuelModalMode = 'MODIFIER', rates, callbacks, qbo_tags }) => {
    const [fuelModalOpen, setFuelModalOpen] = React.useState(false);

    const handleFuelModalSubmit = (params) => {
        callbacks.setAllFuelRates(generateFuelBuckets({ ...params, isPercentage }), { type });
    };

    return (
        <>
            <Grid
                container
                direction="row"
                css={css`
                    margin-bottom: 16px;
                    flex: 0;
                    flex-basis: 0;
                    align-items: center;
                `}
            >
                <QboTagField
                    options={qbo_tags}
                    value={rates?.[type]?.[0]?.qbo_tag}
                    onCommit={(qbo) => {
                        callbacks.updateQBO({
                            qbo,
                            types: [type],
                        });
                    }}
                    css={css`
                        width: 300px;
                    `}
                />
            </Grid>
            {(rates?.[type] || []).map(({ min, max, rate }, idx) => {
                return (
                    <Grid
                        key={`rate-${idx}`}
                        container
                        direction="row"
                        css={css`
                            margin-bottom: 16px;
                            flex: 0;
                            flex-basis: 0;
                        `}
                    >
                        <Grid
                            container
                            item
                            direction="column"
                            css={css`
                                flex: 0;
                                justify-content: center;
                                margin-right: 12px;
                            `}
                        >
                            <Body1>{dollarFormatter.format(min)}</Body1>
                        </Grid>
                        <Grid
                            container
                            item
                            direction="column"
                            css={css`
                                flex: 0;
                                justify-content: center;
                                margin-right: 12px;
                            `}
                        >
                            <ArrowForwardIcon />
                        </Grid>
                        <Grid
                            container
                            item
                            direction="column"
                            xs={3}
                            css={css`
                                justify-content: center;
                                margin-right: 12px;
                            `}
                        >
                            {typeof max === 'string' && max.toLowerCase() === 'infinity' ? (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    disabled
                                    value={max}
                                    error={!max || max <= min}
                                />
                            ) : (
                                <CurrencyTextField
                                    variant="outlined"
                                    size="small"
                                    value={max || 0}
                                    error={!max || max <= min}
                                    onChange={(_, val) => {
                                        callbacks.setFuelRate({
                                            bucket: idx,
                                            max: val,
                                            type,
                                        });
                                    }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                    }}
                                />
                            )}
                        </Grid>
                        <Grid
                            container
                            item
                            direction="column"
                            css={css`
                                flex: 0;
                                justify-content: center;
                                margin-right: 12px;
                            `}
                        >
                            =
                        </Grid>
                        <Grid
                            container
                            item
                            direction="column"
                            xs={3}
                            css={css`
                                justify-content: center;
                                margin-right: 12px;
                            `}
                        >
                            <CurrencyTextField
                                variant="outlined"
                                size="small"
                                value={rate >= 0 ? (isPercentage ? rate * 100 : rate) : 0}
                                error={!(rate >= 0)}
                                onChange={(_, val) => {
                                    callbacks.setFuelRate({
                                        bucket: idx,
                                        rate: isPercentage ? val / 100 : val,
                                        type,
                                    });
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">{isPercentage ? '%' : '$'}</InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        {rates?.[type]?.length - 1 === idx ? (
                            <Grid
                                container
                                item
                                direction="column"
                                css={css`
                                    flex: 0;
                                    justify-content: center;
                                `}
                            >
                                <IconButton
                                    onClick={() => {
                                        callbacks.deleteFuelRate({
                                            bucket: idx,
                                            type,
                                        });
                                    }}
                                    css={css`
                                        padding: 4px;
                                    `}
                                >
                                    <DeleteForeverOutlined color="error" />
                                </IconButton>
                            </Grid>
                        ) : null}
                    </Grid>
                );
            })}
            <Grid
                container
                direction="row"
                css={css`
                    margin-bottom: 16px;
                    flex: 0;
                    flex-basis: 0;
                `}
            >
                <PrimaryButton
                    onClick={() => callbacks.addFuelRate({ type })}
                    css={css`
                        margin-right: 16px;
                    `}
                >
                    Add Row
                </PrimaryButton>
                <PrimaryButton onClick={() => setFuelModalOpen(true)}>Add Calculated FSC</PrimaryButton>
            </Grid>
            <FuelModal
                mode={fuelModalMode}
                open={fuelModalOpen}
                onClose={() => setFuelModalOpen(false)}
                onSubmit={handleFuelModalSubmit}
            />
        </>
    );
};

export default FuelBucketEditor;
